// Base44-compatible REST shim that talks to our FastAPI backend.
// Preserves the same API surface that the existing app code expects:
//   base44.auth.*, base44.entities.<Name>.*, base44.functions.invoke(...),
//   base44.integrations.Core.*, base44.users.*
import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ta_access_token";
const REFRESH_KEY = "ta_refresh_token";

// ---------- token storage ----------
const tokenStore = {
  get: () =>
    (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_KEY)) ||
    null,
  set: (t) => {
    if (typeof window !== "undefined") {
      if (t) window.localStorage.setItem(TOKEN_KEY, t);
      else window.localStorage.removeItem(TOKEN_KEY);
    }
  },
  clear: () => {
    if (typeof window !== "undefined") window.localStorage.removeItem(TOKEN_KEY);
  },
};

const refreshStore = {
  get: () =>
    (typeof window !== "undefined" && window.localStorage.getItem(REFRESH_KEY)) ||
    null,
  set: (t) => {
    if (typeof window !== "undefined") {
      if (t) window.localStorage.setItem(REFRESH_KEY, t);
      else window.localStorage.removeItem(REFRESH_KEY);
    }
  },
  clear: () => {
    if (typeof window !== "undefined")
      window.localStorage.removeItem(REFRESH_KEY);
  },
};

function persistAuth(data) {
  if (data?.access_token) tokenStore.set(data.access_token);
  if (data?.refresh_token) refreshStore.set(data.refresh_token);
  return data;
}

function clearAuth() {
  tokenStore.clear();
  refreshStore.clear();
}

// ---------- axios instance ----------
const http = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});
http.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Single in-flight refresh promise to dedupe concurrent 401s.
let refreshPromise = null;
async function performRefresh() {
  const rt = refreshStore.get();
  // Use credentials so refresh works via cookie or local refresh token
  const { data } = await axios.post(
    `${API_BASE}/auth/refresh`,
    { refresh_token: rt || "" },
    { withCredentials: true }
  );
  if (data?.access_token) tokenStore.set(data.access_token);
  return data?.access_token || null;
}

http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const isAuthEndpoint =
      typeof cfg.url === "string" &&
      (cfg.url.includes("/auth/login") ||
        cfg.url.includes("/auth/register") ||
        cfg.url.includes("/auth/refresh") ||
        cfg.url.includes("/auth/logout"));
    // Try a transparent refresh-and-retry exactly once on a 401.
    if (status === 401 && !cfg.__retried && !isAuthEndpoint && refreshStore.get()) {
      cfg.__retried = true;
      try {
        if (!refreshPromise) refreshPromise = performRefresh();
        const newAccess = await refreshPromise;
        refreshPromise = null;
        if (newAccess) {
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${newAccess}`;
          return http(cfg);
        }
      } catch (_) {
        refreshPromise = null;
        // Refresh itself failed (revoked / unknown token) → fall through and
        // surface a proper auth error so AuthContext logs the user out.
        clearAuth();
      }
    }
    // Transient-error retry. During platform deploys Cloudflare can return
    // 520 (origin dropped/empty), and brief upstream restarts can produce
    // 502/503/504. Retrying with exponential backoff smooths the user
    // experience so a login or any API call doesn't visibly fail during
    // the ~60s deploy window. Network-level failures (no response at all)
    // are also covered.
    const isTransient =
      !err?.response /* network error */ ||
      [502, 503, 504, 520, 521, 522, 524].includes(status);
    const attempt = cfg.__transientAttempt || 0;
    const MAX_TRANSIENT_RETRIES = 3;
    // Only retry idempotent-feeling verbs OR /auth/login (it's safe to retry
    // — a duplicate hit just gets the same response).
    const method = (cfg.method || "get").toLowerCase();
    const safeMethods = ["get", "head", "options", "post"];
    if (isTransient && attempt < MAX_TRANSIENT_RETRIES && safeMethods.includes(method) && !cfg.__noTransientRetry) {
      cfg.__transientAttempt = attempt + 1;
      const delayMs = Math.min(8000, 750 * Math.pow(2, attempt)); // 750, 1500, 3000
      await new Promise((r) => setTimeout(r, delayMs));
      return http(cfg);
    }
    const detail = err?.response?.data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((d) => d?.msg || JSON.stringify(d)).join(", ")
        : detail?.msg ||
          err?.response?.data?.message ||
          err?.message ||
          "Request failed";
    const e = new Error(msg);
    e.status = err?.response?.status;
    e.data = err?.response?.data;
    return Promise.reject(e);
  }
);

// ---------- auth ----------
const auth = {
  me: async () => (await http.get("/auth/me")).data,
  loginViaEmailPassword: async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    return persistAuth(data);
  },
  register: async ({ email, password, full_name }) => {
    const { data } = await http.post("/auth/register", {
      email,
      password,
      full_name,
    });
    return persistAuth(data);
  },
  setToken: (t) => tokenStore.set(t),
  getToken: () => tokenStore.get(),
  ssoToken: async () => {
    const { data } = await http.post("/auth/sso-token");
    return data?.sso_token || null;
  },
  logout: async (redirectTo) => {
    // Revoke refresh token and clear cross-domain session cookies
    const rt = refreshStore.get();
    try {
      await http.post("/auth/logout", { refresh_token: rt || "" });
    } catch (_) {
      /* fire-and-forget */
    }
    clearAuth();
    if (redirectTo) {
      try {
        window.location.href = redirectTo;
      } catch (_) {
        /* ignore */
      }
    }
  },
  redirectToLogin: (fromUrl) => {
    const next =
      fromUrl && encodeURIComponent(fromUrl);
    window.location.href = `/login${next ? `?next=${next}` : ""}`;
  },
  updateMe: async (update) => {
    const { data } = await http.patch("/auth/me", update);
    return data;
  },
  resetPasswordRequest: async (email) => {
    const { data } = await http.post("/auth/forgot-password", { email });
    return data;
  },
  resetPassword: async ({ resetToken, newPassword }) => {
    const { data } = await http.post("/auth/reset-password", {
      token: resetToken,
      new_password: newPassword,
    });
    return data;
  },
  // OTP is not configured in this deployment; keep stubs so existing UI works.
  verifyOtp: async () => ({ access_token: tokenStore.get() }),
  resendOtp: async () => ({ ok: true }),
  // Continue with Google → currently disabled.
  loginWithProvider: (provider) => {
    alert(`${provider || "Social"} sign-in is not enabled in this deployment.`);
  },
  // Exchange the Emergent session_id for our own JWT + user.
  // (Kept for the /auth/callback route; safe to leave even when the button is hidden.)
  completeGoogleLogin: async (sessionId) => {
    const { data } = await http.post("/auth/google/callback", { session_id: sessionId });
    return persistAuth(data);
  },
};

// ---------- entities ----------
const makeEntity = (name) => ({
  list: async (sortBy, limit) => {
    const params = {};
    if (sortBy) params.sort = sortBy;
    if (limit) params.limit = limit;
    const { data } = await http.get(`/entities/${name}`, { params });
    return data;
  },
  filter: async (where = {}, sortBy, limit) => {
    const params = {};
    if (sortBy) params.sort = sortBy;
    if (limit) params.limit = limit;
    const { data } = await http.post(`/entities/${name}/filter`, where || {}, {
      params,
    });
    return data;
  },
  get: async (id) => (await http.get(`/entities/${name}/${id}`)).data,
  create: async (payload) =>
    (await http.post(`/entities/${name}`, payload)).data,
  update: async (id, payload) =>
    (await http.patch(`/entities/${name}/${id}`, payload)).data,
  delete: async (id) =>
    (await http.delete(`/entities/${name}/${id}`)).data,
});

const ENTITY_NAMES = [
  "BodyPartCategory",
  "ChatMessage",
  "CheckIn",
  "Exercise",
  "Exercises", // alias used in some screens
  "ExerciseCompletion",
  "FitnessGoal",
  "MealCompletion",
  "MealLog",
  "WorkoutSession",
  "MealPlan",
  "MealPlanTemplate",
  "ProgressLog",
  "SavedPlan",
  "Trainer",
  "TrainingSession",
  "User",
  "UserWeeklyMealPlan",
  "UserWeeklyWorkoutPlan",
  "WorkoutCompletion",
  "WorkoutPlan",
  "WorkoutTemplate",
];
const entities = Object.fromEntries(ENTITY_NAMES.map((n) => [n, makeEntity(n)]));

// ---------- functions ----------
const functions = {
  invoke: async (name, payload) => {
    const { data } = await http.post(`/functions/${name}`, payload || {});
    return { data };
  },
};

// ---------- integrations.Core ----------
const integrations = {
  Core: {
    UploadFile: async ({ file }) => {
      const fd = file instanceof FormData ? file : new FormData();
      if (!(file instanceof FormData)) {
        const filename = file?.name || "photo.jpg";
        fd.append("file", file, filename);
      }
      const { data } = await http.post("/upload", fd);
      const url = data?.file_url || data?.url;
      return { file_url: url, url, ...data };
    },
    InvokeLLM: async ({ prompt, system, ...rest }) => {
      const { data } = await http.post("/ai/invoke", {
        prompt,
        system,
        ...rest,
      });
      return data;
    },
    EstimateMacros: async ({ food, portion }) => {
      const { data } = await http.post("/ai/estimate-macros", { food, portion });
      return data; // { calories, protein_g, carbs_g, fats_g }
    },
    ScanMeal: async ({ image_base64, mime_type }) => {
      // Vision call can take 10–15 s on a slow connection — bump the
      // per-request timeout so the axios default (no timeout) is fine,
      // but explicitly setting `timeout: 60000` here documents intent.
      const { data } = await http.post(
        "/ai/scan-meal",
        { image_base64, mime_type },
        { timeout: 60000 },
      );
      return data; // { food_name, portion, calories, protein_g, carbs_g, fats_g, confidence }
    },
  },
};

// ---------- users (admin) ----------
const users = {
  inviteUser: async ({ email, role }) => {
    const { data } = await http.post("/admin/invite-user", { email, role });
    return data;
  },
};

export const base44 = {
  auth,
  entities,
  functions,
  integrations,
  users,
  http,
};
